import React, { memo, useId, useImperativeHandle, forwardRef, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RotatingLines } from 'react-loader-spinner';
import clsx from 'clsx';

import Icon from '../../icons/Icon';
import { Input } from '../common-inputs';
import { ICONS } from '../../icons';
import useBodyClicker from '../../hooks/useBodyClicker';
import DropdownClickOutsideController from './controllers/ClickOutsideController';
import ScrollIntoViewController from './controllers/ScrollIntoViewController';
import useGroupedDropdown from './hooks/useGroupedDropdown';
import DropdownMenu from './components/DropdownMenu';
import { PORTAL_Z_INDEX } from './constants';

import './CommonGroupedDropdown.scss';

const CommonGroupedDropdown = forwardRef(function CommonGroupedDropdown(
  {
    groups = [],
    isFilterable = false,
    isLoading = false,
    onSelect,
    disabled = false,
    value = '',
    placeholder,
    maxWidth,
    LeftIcon = null,
    withIcons = false,
    isEmojiIcons,
    isTop: isTopProp,
    isBorder = false,
    onBlur,
    onFocus,
    name,
    isError,
    isReadOnly,
    usePortal = true,
    isShowGroupLabel = false,
  },
  ref,
) {
  const listboxId = useId();

  const {
    dropdownOpen,
    setDropdownOpen,
    searchQuery,
    setSearchQuery,
    highlightedIndex,
    setHighlightedIndex,
    wrapperRef,
    portalRef,
    menuRef,
    flatOptions,
    flatFilteredOptions,
    selectedOption,
    filteredGroups,
    hasOptions,
    highlightedOption,
    menuPosition,
    isTop,
  } = useGroupedDropdown({
    groups,
    value,
    isFilterable,
    usePortal,
    isTopProp,
  });

  const handleClickOutside = useCallback(() => {
    setDropdownOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  }, [setDropdownOpen, setSearchQuery, setHighlightedIndex]);

  const dropdownRef = useBodyClicker(() => !usePortal && handleClickOutside());

  useEffect(() => {
    if (!dropdownOpen || flatFilteredOptions.length === 0) return;
    const len = flatFilteredOptions.length;
    setHighlightedIndex((i) => (i < 0 ? 0 : i >= len ? len - 1 : i));
  }, [dropdownOpen, flatFilteredOptions.length, setHighlightedIndex]);

  const handleSelectOption = useCallback(
    (optionValue) => {
      setDropdownOpen(false);
      setHighlightedIndex(-1);
      onSelect?.(optionValue);
      setSearchQuery('');
    },
    [onSelect, setDropdownOpen, setHighlightedIndex, setSearchQuery],
  );

  const handleChangeSearch = useCallback(
    (newSearch) => {
      if (isFilterable) {
        setSearchQuery(newSearch);
        setHighlightedIndex(0);
      }
    },
    [isFilterable, setSearchQuery, setHighlightedIndex],
  );

  const openDropdown = useCallback(() => {
    if (!disabled && !isReadOnly) {
      setDropdownOpen(true);
      const idx = flatOptions.findIndex((o) => o.value === value);
      setHighlightedIndex(idx >= 0 ? idx : 0);
    }
  }, [disabled, isReadOnly, setDropdownOpen, setHighlightedIndex, flatOptions, value]);

  const handleInputClick = useCallback(() => {
    if (disabled || isReadOnly) return;
    if (dropdownOpen) {
      handleClickOutside();
    } else {
      openDropdown();
    }
  }, [disabled, isReadOnly, dropdownOpen, handleClickOutside, openDropdown]);

  useImperativeHandle(ref, () => ({
    focus: () => wrapperRef.current?.querySelector('input')?.focus(),
    blur: () => wrapperRef.current?.querySelector('input')?.blur(),
  }));

  const style = useMemo(() => (maxWidth != null ? { maxWidth } : undefined), [maxWidth]);

  const coreLabel = isShowGroupLabel
    ? (selectedOption?.groupLabel ? selectedOption?.groupLabel + ': ' : '') + selectedOption?.label
    : selectedOption?.label ?? '';

  const displayValue = dropdownOpen && isFilterable ? searchQuery : coreLabel;
  const rootRef = usePortal ? wrapperRef : dropdownRef;

  const showMenu = dropdownOpen && (
    <DropdownMenu
      listboxId={listboxId}
      isTop={isTop}
      isBorder={isBorder}
      hasOptions={hasOptions}
      filteredGroups={filteredGroups}
      searchQuery={searchQuery}
      withIcons={withIcons}
      isEmojiIcons={isEmojiIcons}
      highlightedOption={highlightedOption}
      onSelectOption={handleSelectOption}
      menuRef={menuRef}
    />
  );

  return (
    <div
      ref={rootRef}
      className='common-grouped-dropdown'
      style={style}
      role='combobox'
      aria-expanded={dropdownOpen}
      aria-haspopup='listbox'
      aria-controls={dropdownOpen ? listboxId : undefined}
      aria-disabled={disabled}
      aria-readonly={isReadOnly ?? undefined}
      aria-invalid={isError ?? undefined}
      tabIndex={-1}>
      <Input
        onClick={handleInputClick}
        extraInputClass={clsx(!disabled && !isFilterable && !isReadOnly && 'common-grouped-dropdown--pointer')}
        value={displayValue}
        placeholder={placeholder}
        isReadOnly={!isFilterable || disabled || isReadOnly}
        debounce={isFilterable}
        onChange={handleChangeSearch}
        size='sm'
        disabled={disabled}
        LeftIcon={LeftIcon || undefined}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
        isError={isError}
        RightIcon={
          isLoading ? (
            <RotatingLines strokeColor='#fdbf47' height={10} width={20} />
          ) : (
            <Icon
              icon={ICONS.CaretUp}
              disableCommonStyles
              className={clsx(
                'common-grouped-dropdown_icon',
                dropdownOpen ? 'common-grouped-dropdown_icon--up' : 'common-grouped-dropdown_icon--down',
              )}
            />
          )
        }
      />

      <DropdownClickOutsideController
        usePortal={usePortal}
        dropdownOpen={dropdownOpen}
        wrapperRef={wrapperRef}
        portalRef={portalRef}
        onClose={handleClickOutside}
      />
      <ScrollIntoViewController
        menuRef={menuRef}
        highlightedIndex={highlightedIndex}
        flatFilteredOptions={flatFilteredOptions}
        listboxId={listboxId}
      />

      {usePortal && dropdownOpen && menuPosition
        ? ReactDOM.createPortal(
            <div
              ref={portalRef}
              className='common-grouped-dropdown_portal'
              style={{
                position: 'fixed',
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                width: menuPosition.width,
                zIndex: PORTAL_Z_INDEX,
              }}>
              {showMenu}
            </div>,
            document.body,
          )
        : !usePortal && showMenu}
    </div>
  );
});

CommonGroupedDropdown.displayName = 'CommonGroupedDropdown';

export default memo(CommonGroupedDropdown);
